import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { BasicTabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { confirmAction, confirmDestructive, modal } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import {
    SchemaEditorTab,
    SchemaGeneralTab,
    SchemaPreviewTab,
    useSchemaForm,
} from '@client/components/attestations/legacy-schema-editor';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { trpc } from '@client/trpc/client';
import { error as logError } from '@client/utils/logger';
import { SCHEDULED_USER_ATTESTATION_FLAG } from '@shared/constants/business/user-attestation';
import type { DynamicFormSchema } from '@shared/schemas/dynamic-forms/dynamic-form-schema';
import { keepPreviousData, skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Tag } from 'antd';
import { Archive, FileCode, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/compliance/attestations/user-attestations/edit/$id',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Edit form',
        },
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    const { id } = Route.useParams();
    return (
        <PageController route={Route}>
            <FormSchemaEdit key={String(id)} />
        </PageController>
    );
}

function FormSchemaEdit() {
    const formId = useId();
    const { trpcUtils } = useTrpc();
    const { id } = Route.useParams();
    const { mutate: updateMutate } = trpc.formSchema.updateFormSchema.useMutation();
    const { mutate: publishMutate } = trpc.formSchema.publishFormSchema.useMutation();
    const { mutate: archiveMutate } = trpc.formSchema.archiveFormSchema.useMutation();
    const { mutate: deleteMutate } = trpc.formSchema.deleteFormSchema.useMutation();
    const navigate = useNavigate();
    const hasScheduledAttestations = useFeatureFlag(SCHEDULED_USER_ATTESTATION_FLAG);
    const [form] = Form.useForm();
    const formDirty = useFormDirty();

    const [activeTab, setActiveTab] = useState('general');

    /**
     * A save of a published or archived record creates a new draft. The editor rebinds to that
     * draft even when later edits stop the redirect: `updateDraft` clones a published record on
     * every call, so a second save against the route's id would create a third version instead of
     * updating the draft that was just created.
     */
    const [createdDraftId, setCreatedDraftId] = useState<bigint>();
    const activeSchemaId = createdDraftId ?? id;

    const schemaQuery = trpc.formSchema.getFormSchema.useQuery(
        activeSchemaId ? { schemaId: activeSchemaId } : skipToken,
        // Rebinding to a created draft changes the query key. Without the previous record the body
        // would swap to a spinner for the round trip, unmounting the form under a user who is
        // mid-keystroke — the very late edit the rebind exists to keep.
        { placeholderData: keepPreviousData },
    );
    const schemaData = schemaQuery.data;
    /** Set by a successful delete so the close does not refetch the record that no longer exists. */
    const deletedSchemaIdRef = useRef<bigint | undefined>(undefined);

    const {
        schemaJson,
        parsedSchema,
        parseError,
        parseErrorKind,
        copied,
        formStore,
        formatJson,
        copySchema,
        updateSchema,
        updateSchemaJson,
        setSchemaJson,
    } = useSchemaForm();

    const initialisedRef = useRef(false);

    // Initialise once per record; query refreshes must not overwrite subsequent edits.
    useEffect(() => {
        if (schemaData && !initialisedRef.current) {
            initialisedRef.current = true;
            const schema = schemaData.schema as DynamicFormSchema;
            setSchemaJson(JSON.stringify(schema, null, 2));
            form.setFieldsValue({
                formTitle: schemaData.formTitle,
                schemaKey: schemaData.schemaKey,
                tags: schemaData.tags || [],
                notes: schemaData.versionNotes || '',
                allowOverdueSubmission: schemaData.allowOverdueSubmission,
            });
        }
    }, [schemaData, schemaData?.id, form, setSchemaJson]);

    const handleSchemaGenerated = useCallback(
        (schema: DynamicFormSchema) => {
            formDirty.markDirty();
            updateSchema(schema);

            if (schema.title && schema.title !== form.getFieldValue('formTitle')) {
                form.setFieldValue('formTitle', schema.title);
            }

            setActiveTab('preview');
        },
        [form, formDirty, updateSchema],
    );

    const handleFormSubmit = useCallback(async () => {
        message.success('Form submitted successfully!');
    }, []);

    const handleReset = useCallback(() => {
        if (formStore) {
            formStore.getState().reset();
        }
    }, [formStore]);

    const handleArchive = (onClose: () => void) => {
        confirmDestructive({
            title: 'Archive form?',
            // Archiving the active version leaves the form with nothing to send (CUR-1473).
            content: [
                `"${schemaData?.formTitle}" will no longer be available to send. Attestations already sent are not affected.`,
                ...(hasScheduledAttestations
                    ? ['Any scheduled attestation that uses this form will be disabled at its next send.']
                    : []),
                'To bring the form back, open it, save a new version and publish it.',
            ].join(' '),
            okText: 'Archive',
            onOk: () => {
                message.loading('Archiving...');
                archiveMutate(
                    { schemaId: activeSchemaId, reason: 'Archived by user' },
                    {
                        onSuccess() {
                            message.success('Form archived successfully');
                            onClose();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const handleDelete = (onClose: () => void) => {
        if (schemaData?.status !== 'DRAFT') {
            modal.warning({
                title: 'Cannot delete',
                content:
                    'Only draft forms can be deleted. Published or archived forms must be retained for audit purposes.',
            });
            return;
        }

        confirmDestructive({
            title: 'Delete form?',
            content: `Are you sure you want to delete "${schemaData?.formTitle}"? This action cannot be undone.`,
            okText: 'Delete',
            onOk: () => {
                message.loading('Deleting...');
                deleteMutate(
                    { schemaId: activeSchemaId },
                    {
                        onSuccess() {
                            message.success('Form deleted successfully');
                            deletedSchemaIdRef.current = activeSchemaId;
                            onClose();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const isDraft = schemaData?.status === 'DRAFT';
    const isPublished = schemaData?.status === 'PUBLISHED';
    const isArchived = schemaData?.status === 'ARCHIVED';

    const handleSaveDraft = async () => {
        try {
            const values = await form.validateFields();
            if (!parsedSchema) {
                message.error('Please fix schema errors before saving');
                return;
            }

            // Ensure the schema title matches the form title
            const schemaToSave = {
                ...parsedSchema,
                title: values.formTitle || parsedSchema.title,
            };

            const markSaved = formDirty.captureSave();
            message.loading('Saving...');

            updateMutate(
                {
                    schemaId: activeSchemaId,
                    schema: schemaToSave,
                    tags: values.tags || [],
                    allowOverdueSubmission: values.allowOverdueSubmission ?? true,
                    versionNotes: values.notes || undefined,
                },
                {
                    onSuccess() {
                        markSaved();
                        message.success('Form updated successfully');
                        void schemaQuery.refetch();
                    },
                    onError(error) {
                        errorMessage.show(error);
                    },
                },
            );
        } catch (error) {
            logError(error);
        }
    };

    const handlePublishExisting = async (onClose: () => void) => {
        try {
            const values = await form.validateFields();
            if (!parsedSchema) {
                message.error('Please fix schema errors before saving');
                return;
            }

            // Ensure the schema title matches the form title
            const schemaToSave = {
                ...parsedSchema,
                title: values.formTitle || parsedSchema.title,
            };

            confirmAction({
                title: 'Publish form?',
                content: 'Publishing will make this version active. Are you sure?',
                okText: 'Publish',
                onOk: () => {
                    const markSaved = formDirty.captureSave();
                    message.loading('Publishing...');

                    // CUR-1512: publish the draft as it stands on screen. Publishing without
                    // saving first would drop the editor's values — including "Allow submission
                    // after overdue" — and send the previously saved setting to every new
                    // attestation. Only publish once that save has succeeded.
                    updateMutate(
                        {
                            schemaId: activeSchemaId,
                            schema: schemaToSave,
                            tags: values.tags || [],
                            allowOverdueSubmission: values.allowOverdueSubmission ?? true,
                            versionNotes: values.notes || undefined,
                        },
                        {
                            onSuccess(saved) {
                                markSaved();
                                setCreatedDraftId(saved.id);
                                publishMutate(
                                    { schemaId: saved.id },
                                    {
                                        onSuccess() {
                                            message.success('Form published successfully');
                                            onClose();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                );
                            },
                            onError(error) {
                                errorMessage.show(error);
                            },
                        },
                    );
                },
            });
        } catch (error) {
            logError(error);
        }
    };

    const handleSaveNewVersion = async () => {
        try {
            const values = await form.validateFields();
            if (!parsedSchema) {
                message.error('Please fix schema errors before saving');
                return;
            }

            // Ensure the schema title matches the form title
            const schemaToSave = {
                ...parsedSchema,
                title: values.formTitle || parsedSchema.title,
            };

            const markSaved = formDirty.captureSave();
            message.loading('Creating new version...');

            updateMutate(
                {
                    schemaId: activeSchemaId,
                    schema: schemaToSave,
                    tags: values.tags || [],
                    allowOverdueSubmission: values.allowOverdueSubmission ?? true,
                    versionNotes: values.notes || undefined,
                },
                {
                    async onSuccess(newVersion) {
                        const savedCurrentDraft = markSaved();
                        // Check if a new version was actually created
                        if (newVersion.id === activeSchemaId && newVersion.version === schemaData?.version) {
                            message.info('No changes detected - version not created');
                            return;
                        }

                        message.success(`New draft version v${newVersion.version} created successfully`);

                        // Keep the created draft regardless of later edits: the next save must
                        // update it, not clone the published record again.
                        setCreatedDraftId(newVersion.id);

                        await trpcUtils.formSchema.invalidate();

                        if (!savedCurrentDraft || !markSaved()) return;

                        navigate({
                            to: '/console/$orgId/configuration/compliance/attestations/user-attestations/edit/$id',
                            params: { orgId: newVersion.orgId, id: newVersion.id },
                            replace: true,
                        });
                    },
                    onError(error) {
                        errorMessage.show(error);
                    },
                },
            );
        } catch (error) {
            logError(error);
        }
    };

    const handleUpdateAndPublish = async (onClose: () => void) => {
        try {
            const values = await form.validateFields();
            if (!parsedSchema) {
                message.error('Please fix schema errors before saving');
                return;
            }

            // Ensure the schema title matches the form title
            const schemaToSave = {
                ...parsedSchema,
                title: values.formTitle || parsedSchema.title,
            };

            confirmAction({
                title: 'Update and publish?',
                content: 'This will create a new version and immediately publish it as the active version.',
                okText: 'Update and publish',
                onOk: () => {
                    message.loading('Updating and publishing...');

                    updateMutate(
                        {
                            schemaId: activeSchemaId,
                            schema: schemaToSave,
                            tags: values.tags || [],
                            allowOverdueSubmission: values.allowOverdueSubmission ?? true,
                            versionNotes: values.notes || undefined,
                        },
                        {
                            onSuccess(newVersion) {
                                setCreatedDraftId(newVersion.id);
                                // After creating new version, publish it
                                publishMutate(
                                    { schemaId: newVersion.id },
                                    {
                                        onSuccess() {
                                            message.success('Form updated and published successfully');
                                            onClose();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                );
                            },
                            onError(error) {
                                errorMessage.show(error);
                            },
                        },
                    );
                },
            });
        } catch (error) {
            logError(error);
        }
    };

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            size="xLarge"
            title={
                <div className="flex items-center gap-3">
                    <span>{schemaData?.formTitle || 'Edit form'}</span>
                    {schemaData && (
                        <>
                            <span className="text-subtle-foreground">•</span>
                            <Tag variant="outlined" color="default" className="font-mono">
                                v{schemaData.version}
                            </Tag>
                            <span className="text-subtle-foreground">•</span>
                            <StatusBadge status={schemaData.status} module="formSchema" />
                        </>
                    )}
                </div>
            }
            afterClose={() => {
                form.resetFields();
                const deletedId = deletedSchemaIdRef.current;
                if (deletedId === undefined) {
                    void trpcUtils.formSchema.invalidate();
                } else {
                    // Skip the deleted record: the editor is still mounted, so refetching its
                    // active query would land a NOT_FOUND before unmount.
                    void trpcUtils.formSchema.invalidate(undefined, {
                        predicate: (query) =>
                            (query.queryKey[1] as { input?: { schemaId?: bigint } } | undefined)?.input?.schemaId !==
                            deletedId,
                    });
                }
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose, requestClose }) => (
                <div className="flex w-full items-center justify-between">
                    <Space>
                        {isDraft && (
                            <Button danger icon={<Trash2 size={16} />} onClick={() => handleDelete(onClose)}>
                                Delete
                            </Button>
                        )}
                    </Space>
                    <Space>
                        <Button onClick={requestClose}>Close</Button>

                        {isDraft && (
                            <>
                                <Button
                                    type="secondary"
                                    onClick={() => handlePublishExisting(onClose)}
                                    disabled={!parsedSchema}
                                >
                                    Publish
                                </Button>
                                <Button type="primary" onClick={handleSaveDraft} disabled={!parsedSchema}>
                                    Save Draft
                                </Button>
                            </>
                        )}

                        {isPublished && (
                            <>
                                <Button
                                    type="secondary"
                                    icon={<Archive size={16} />}
                                    onClick={() => handleArchive(onClose)}
                                >
                                    Archive
                                </Button>
                                <Button type="secondary" onClick={handleSaveNewVersion} disabled={!parsedSchema}>
                                    Save New Version
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={() => handleUpdateAndPublish(onClose)}
                                    disabled={!parsedSchema}
                                >
                                    Update and publish
                                </Button>
                            </>
                        )}

                        {isArchived && (
                            <Button type="primary" onClick={handleSaveNewVersion} disabled={!parsedSchema}>
                                Save New Version
                            </Button>
                        )}
                    </Space>
                </div>
            )}
        >
            {() => (
                <div className="flex h-full flex-col">
                    {/* Only before there is a record to show: a refetch — or the rebind to a created
                        draft, which loads under a new query key — must not unmount the form under a
                        user who is mid-edit. */}
                    {schemaQuery.isLoading && !schemaData ? (
                        <Loading size="large" />
                    ) : (
                        <Form id={formId} form={form} {...formDirty.formProps} layout="vertical">
                            <BasicTabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                items={[
                                    {
                                        key: 'general',
                                        label: 'Form settings',
                                        forceRender: true,
                                        children: (
                                            <SchemaGeneralTab
                                                mode="edit"
                                                schemaKey={schemaData?.schemaKey}
                                                version={schemaData?.version}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'editor',
                                        label: (
                                            <span className="flex items-center gap-2">
                                                <FileCode size={16} />
                                                Form editor
                                            </span>
                                        ),
                                        forceRender: true,
                                        children: (
                                            <SchemaEditorTab
                                                schemaJson={schemaJson}
                                                parseError={parseError}
                                                parseErrorKind={parseErrorKind}
                                                copied={copied}
                                                existingSchema={parsedSchema}
                                                onSchemaChange={(value) => {
                                                    formDirty.markDirty();
                                                    updateSchemaJson(value);
                                                }}
                                                onFormatJson={formatJson}
                                                onCopySchema={copySchema}
                                                onSchemaGenerated={handleSchemaGenerated}
                                                onPromptChange={formDirty.markDirty}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'preview',
                                        label: 'Form preview',
                                        children: (
                                            <SchemaPreviewTab
                                                parsedSchema={parsedSchema}
                                                formStore={formStore}
                                                onReset={handleReset}
                                                onSubmit={handleFormSubmit}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </Form>
                    )}
                </div>
            )}
        </OverlayPage>
    );
}
